/**
 * Typed HTTP client for the application's backend.
 *
 * All requests are sent with `credentials: "include"` so the httpOnly session
 * cookie issued by @userabuser/app-auth-routes is attached automatically.
 */

import type {
  ActivityEntry,
  AttachmentDraft,
  CommentRecord,
  CreateJobInput,
  CreateQuoteInput,
  InvoicePaymentsSummary,
  InvoiceRecord,
  JobRecord,
  JobValueTotals,
  MentionableUser,
  MemberView,
  QuoteRecord,
  UpdateJobInput,
  UpdateQuoteInput,
} from "../features/jobs/types";
import type { ScopeDraft } from "../features/costing/types";
import type { ClientContact, ClientRecord, ClientSuggestion, CreateClientDraft, CreateContactDraft } from "../features/clients/types";
import type { PlaceSuggestion } from "../features/jobs/types";
import type { PortalInvoice, PortalProfile, PortalQuote, PortalUserLink } from "../features/portal/types";
import type { DocumentListRecord } from "../features/documents/types";
import type { ContractorDetails, ContractorInput, ServiceRecord } from "../../shared/contractors";
import type { ScheduleData, ScheduledVisit, StaffUnavailability } from "../features/scheduling/types";
export type { ContractorDetails, ContractorInput, ServiceRecord } from "../../shared/contractors";

export class ApiError extends Error {
  constructor(message: string, public fields: Record<string, string> = {}) { super(message); }
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  status: string;
  systemRole: string;
  roles: string[];
  permissions: string[];
  /** True only for the env-provisioned master account outside impersonation. */
  isMasterAdmin: boolean;
  /** Present when the master session is currently operating as this user. */
  impersonatedBy?: {
    id: string;
    email: string;
    displayName: string;
  };
}

/** An active account the master admin can assume. */
export interface ImpersonationTarget {
  id: string;
  email: string;
  displayName: string;
  systemRole: string;
  roles: string[];
}

export interface BoardColumn {
  id: string;
  title: string;
}

/**
 * Issues a JSON request and normalizes errors into a single Error type.
 *
 * @template T - Expected JSON response shape.
 * @param {string} path - Request path (same origin).
 * @param {RequestInit} [init] - Standard fetch options.
 * @returns {Promise<T>} Parsed response body.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    let fields: Record<string, string> = {};
    try {
      const data = (await response.json()) as { error?: string; fields?: Record<string, string> };
      if (data?.error) message = data.error;
      if (data?.fields) fields = data.fields;
    } catch {
      /* response had no JSON body; keep default message */
    }
    throw new ApiError(message, fields);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Returns the currently authenticated user, or rejects with 401.
 * `/auth/me` wraps the resolved user in `{ user }`; unwrap it so callers get
 * the AuthUser directly (session permissions are re-resolved server-side).
 */
export const getMe = (): Promise<AuthUser> =>
  request<{ user: AuthUser }>("/auth/me").then((body) => body.user);

/** Authenticates with email + password and establishes a session cookie. */
export const login = (email: string, password: string): Promise<void> =>
  request<void>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

/** Clears the session cookie. */
export const logout = (): Promise<void> =>
  request<void>("/auth/logout", { method: "POST" });

/** Lists every active non-master account available to the master admin. */
export const getImpersonationTargets = (): Promise<{ users: ImpersonationTarget[] }> =>
  request<{ users: ImpersonationTarget[] }>("/api/admin/impersonation/users");

/** Starts resolving this master session as the selected target account. */
export const startImpersonation = (userId: string): Promise<void> =>
  request<void>("/api/admin/impersonation", {
    method: "POST",
    body: JSON.stringify({ userId }),
  });

/** Clears the assumed identity and restores the master admin. */
export const stopImpersonation = (): Promise<void> =>
  request<void>("/api/admin/impersonation", { method: "DELETE" });

/** Returns the board column configuration for the jobs board. */
export const getBoard = (): Promise<{ columns: BoardColumn[] }> =>
  request<{ columns: BoardColumn[] }>("/api/board");

/** Returns every persisted job. */
export const getJobs = (): Promise<{ jobs: JobRecord[] }> =>
  request<{ jobs: JobRecord[] }>("/api/jobs");

/** Creates a new job from a minted card id and a validated draft. */
export const createJob = (input: CreateJobInput): Promise<{ job: JobRecord }> =>
  request<{ job: JobRecord }>("/api/jobs", {
    method: "POST",
    body: JSON.stringify(input),
  });

/** Updates an existing job (edit and/or column move). */
export const updateJob = (id: string, input: UpdateJobInput): Promise<{ job: JobRecord }> =>
  request<{ job: JobRecord }>(`/api/jobs/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });

/**
 * Uploads one attachment file for a job. The file is streamed as the raw
 * request body; display name and content type travel as query parameters.
 */
export const uploadAttachment = (jobId: string, file: File): Promise<{ attachment: AttachmentDraft }> =>
  request<{ attachment: AttachmentDraft }>(
    `/api/jobs/${jobId}/attachments?${new URLSearchParams({
      name: file.name,
      type: file.type || "application/octet-stream",
    })}`,
    { method: "POST", body: file, headers: { "Content-Type": "application/octet-stream" } },
  );

/** Deletes a stored job attachment (its DB row and its file on disk). */
export const deleteAttachment = (jobId: string, attachmentId: string): Promise<void> =>
  request<void>(`/api/jobs/${jobId}/attachments/${attachmentId}`, { method: "DELETE" });

/** Headline job value totals (drafts / potential / realized). */
export const getJobTotals = (): Promise<{ totals: JobValueTotals }> =>
  request<{ totals: JobValueTotals }>("/api/jobs/totals");

export interface SaveScheduleVisitInput {
	id?: string;
	jobId: string;
	date: string;
	startTime: string;
	endTime: string;
	phase: string;
	requiredServiceId?: string | null;
	status: "planned" | "tentative" | "cancelled";
	accessStatus: "unknown" | "confirmed" | "issue";
	accessNote: string;
	accessWindowAcknowledged?: boolean;
	revision?: number;
	assignments: { personId: string; startTime: string; endTime: string; travelBeforeMinutes: number }[];
}

/** Returns the internal planner range; portal callers are rejected server-side. */
export const getSchedule = (from: string, to: string): Promise<ScheduleData> =>
	request<ScheduleData>(`/api/schedule?${new URLSearchParams({ from, to })}`);

/** Creates or updates one visit (and its assignment intervals) atomically. */
export const saveScheduleVisit = (input: SaveScheduleVisitInput): Promise<{ visit: ScheduledVisit }> =>
	request("/api/schedule/visits", { method: "POST", body: JSON.stringify(input) });

/** Records or updates one person's unavailability window. */
export const saveStaffUnavailability = (
	input: Omit<StaffUnavailability, "id" | "personName"> & { id?: string },
): Promise<{ unavailability: StaffUnavailability }> =>
	request("/api/schedule/unavailability", { method: "POST", body: JSON.stringify(input) });

/** Every live quote and invoice, enriched with its current job directory fields. */
export const getDocuments = (): Promise<{ documents: DocumentListRecord[] }> =>
  request<{ documents: DocumentListRecord[] }>("/api/documents");

/* ---------- Card activity (audit trail) ---------- */

/**
 * One page of a job's append-only activity trail (newest first). Pass the
 * last entry's `createdAt` as `before` to page further back.
 */
export const getJobActivity = (
  jobId: string,
  before?: string,
): Promise<{ activity: ActivityEntry[] }> =>
  request<{ activity: ActivityEntry[] }>(
    `/api/jobs/${jobId}/activity${before ? `?before=${encodeURIComponent(before)}` : ""}`,
  );

/**
 * Previews the next system-assigned internal job reference (display only).
 * The real number is allocated when the job is created, so a preview can go
 * stale if another job is saved first.
 */
export const getNextInternalRef = (): Promise<{ refInternal: string }> =>
  request<{ refInternal: string }>("/api/jobs/next-ref");

/**
 * Address autocomplete for the property field. The lookup runs on the server so
 * the geocoding key never reaches the browser; results are biased to South
 * Australia by the configured provider.
 */
export const getPlaces = (query: string): Promise<{ places: PlaceSuggestion[] }> =>
  request<{ places: PlaceSuggestion[] }>(`/api/places?q=${encodeURIComponent(query.trim())}`);

/** Client directory (autocomplete): searches clients by name/email/phone. */
export const getClients = (query = ""): Promise<{ clients: ClientSuggestion[] }> =>
  request<{ clients: ClientSuggestion[] }>(
    query.trim() ? `/api/clients?q=${encodeURIComponent(query.trim())}` : "/api/clients",
  );

/** Full client records (with capabilities) for the management screen. */
export const getClientsAdmin = (): Promise<{ clients: ClientRecord[] }> =>
  request<{ clients: ClientRecord[] }>("/api/clients?all=1");

/** Creates a client and returns the full record. */
export const createClient = (draft: CreateClientDraft): Promise<{ client: ClientRecord }> =>
  request<{ client: ClientRecord }>("/api/clients", {
    method: "POST",
    body: JSON.stringify(draft),
  });

/** Updates a client's directory fields; omitted fields keep their value. */
export const updateClient = (
  clientId: string,
  draft: CreateClientDraft,
): Promise<{ client: ClientRecord }> =>
  request<{ client: ClientRecord }>(`/api/clients/${clientId}`, {
    method: "PATCH",
    body: JSON.stringify(draft),
  });

/** Lists contacts for a client. */
export const getClientContacts = (clientId: string): Promise<{ contacts: ClientContact[] }> =>
  request<{ contacts: ClientContact[] }>(`/api/clients/${clientId}/contacts`);

/** Creates a contact under a client. */
export const createClientContact = (
  clientId: string,
  draft: CreateContactDraft,
): Promise<{ contact: ClientContact }> =>
  request<{ contact: ClientContact }>(`/api/clients/${clientId}/contacts`, {
    method: "POST",
    body: JSON.stringify(draft),
  });

/** Updates a contact under a client; omitted fields keep their value. */
export const updateClientContact = (
  clientId: string,
  contactId: string,
  draft: CreateContactDraft,
): Promise<{ contact: ClientContact }> =>
  request<{ contact: ClientContact }>(`/api/clients/${clientId}/contacts/${contactId}`, {
    method: "PATCH",
    body: JSON.stringify(draft),
  });

/** Sets a client's capabilities (full replacement). */
export const setClientCapabilities = (
  clientId: string,
  capabilities: string[],
): Promise<{ capabilities: string[] }> =>
  request<{ capabilities: string[] }>(`/api/clients/${clientId}/capabilities`, {
    method: "PUT",
    body: JSON.stringify({ capabilities }),
  });

/* ---------- Quote & invoice documents ---------- */

/** Lists a job's live quotes, newest version first. */
export const getQuotes = (jobId: string): Promise<{ quotes: QuoteRecord[] }> =>
  request<{ quotes: QuoteRecord[] }>(`/api/jobs/${jobId}/quotes`);

/** Creates the next quote version from the job's current scope. */
export const createQuote = (jobId: string, input: CreateQuoteInput = {}): Promise<{ quote: QuoteRecord }> =>
  request<{ quote: QuoteRecord }>(`/api/jobs/${jobId}/quotes`, {
    method: "POST",
    body: JSON.stringify(input),
  });

/** Updates a draft/sent quote (expiry, notes, and/or line-item replacement). */
export const updateQuote = (quoteId: string, input: UpdateQuoteInput): Promise<{ quote: QuoteRecord }> =>
  request<{ quote: QuoteRecord }>(`/api/quotes/${quoteId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });

/** Sends a draft quote (supersedes other sent quotes on the job). */
export const sendQuote = (quoteId: string): Promise<{ quote: QuoteRecord }> =>
  request<{ quote: QuoteRecord }>(`/api/quotes/${quoteId}/send`, { method: "POST" });

/** Records the client's acceptance of a sent quote. */
export const acceptQuote = (quoteId: string): Promise<{ quote: QuoteRecord }> =>
  request<{ quote: QuoteRecord }>(`/api/quotes/${quoteId}/accept`, { method: "POST" });

/** Records the client's rejection of a sent quote. */
export const rejectQuote = (quoteId: string): Promise<{ quote: QuoteRecord }> =>
  request<{ quote: QuoteRecord }>(`/api/quotes/${quoteId}/reject`, { method: "POST" });

/** Soft-deletes a draft/sent quote. */
export const deleteQuote = (quoteId: string): Promise<{ ok: boolean }> =>
  request<{ ok: boolean }>(`/api/quotes/${quoteId}`, { method: "DELETE" });

/** Generates (or refreshes, while draft) the job's final invoice. Done jobs only. */
export const generateInvoice = (jobId: string): Promise<{ invoice: InvoiceRecord }> =>
  request<{ invoice: InvoiceRecord }>(`/api/jobs/${jobId}/invoice`, { method: "POST" });

/** Returns the job's invoice, or null when none exists yet. */
export const getInvoice = (jobId: string): Promise<{ invoice: InvoiceRecord | null }> =>
  request<{ invoice: InvoiceRecord | null }>(`/api/jobs/${jobId}/invoice`);

/**
 * The invoice builder's payload. The bill-to is whichever of these the author
 * supplied: a job (the invoice attaches to it and takes its reference), a
 * directory client, or a typed name for someone not on file.
 */
export interface CreateInvoiceInput {
  jobId?: string;
  clientId?: string;
  clientName?: string;
  clientEmail?: string;
  propertyAddress?: string;
  scope: ScopeDraft;
  notes?: string;
}

/** Creates a draft invoice from line items authored on the invoice itself. */
export const createInvoice = (input: CreateInvoiceInput): Promise<{ invoice: InvoiceRecord }> =>
  request<{ invoice: InvoiceRecord }>("/api/invoices", {
    method: "POST",
    body: JSON.stringify(input),
  });

/**
 * Reads one invoice by its own id. Distinct from {@link getInvoice}, which
 * looks an invoice up through the job that owns it — an invoice need not have
 * one (DEC-QI-012).
 */
export const getInvoiceById = (invoiceId: string): Promise<{ invoice: InvoiceRecord }> =>
  request<{ invoice: InvoiceRecord }>(`/api/invoices/${invoiceId}`);

/**
 * What an edit may change (DEC-QI-013). Amounts are editable until the first
 * payment lands, after which only `notes` is accepted; the bill-to fields are
 * refused outright on an invoice that belongs to a job, which bills through it.
 */
export interface UpdateInvoiceInput {
  scope?: ScopeDraft;
  notes?: string;
  clientId?: string;
  clientName?: string;
  clientEmail?: string;
  propertyAddress?: string;
}

/** Corrects an existing invoice in place, keeping its number and link. */
export const updateInvoice = (
  invoiceId: string,
  input: UpdateInvoiceInput,
): Promise<{ invoice: InvoiceRecord }> =>
  request<{ invoice: InvoiceRecord }>(`/api/invoices/${invoiceId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });

/** Sends a draft invoice, minting the client's access link. */
export const sendInvoice = (invoiceId: string): Promise<{ invoice: InvoiceRecord }> =>
  request<{ invoice: InvoiceRecord }>(`/api/invoices/${invoiceId}/send`, { method: "POST" });

/** Emails a quote as a PDF to the client + contacts (drafts are sent first). */
export const emailQuote = (
  quoteId: string,
): Promise<{ quote: QuoteRecord; delivered: boolean; recipients: string[] }> =>
  request<{ quote: QuoteRecord; delivered: boolean; recipients: string[] }>(
    `/api/quotes/${quoteId}/email`,
    { method: "POST" },
  );

/** Emails a sent invoice as a PDF to the client + contacts. */
export const emailInvoice = (
  invoiceId: string,
): Promise<{ invoice: InvoiceRecord; delivered: boolean; recipients: string[] }> =>
  request<{ invoice: InvoiceRecord; delivered: boolean; recipients: string[] }>(
    `/api/invoices/${invoiceId}/email`,
    { method: "POST" },
  );

/* ---------- Client portal (Phase B) ---------- */

/** Lists a client's portal users (vendor management). */
export const getClientPortalUsers = (clientId: string): Promise<{ portalUsers: PortalUserLink[] }> =>
  request<{ portalUsers: PortalUserLink[] }>(`/api/clients/${clientId}/portal-users`);

/** Invites a portal user: creates/reuses the auth account and links it to the client. */
export const inviteClientPortalUser = (
  clientId: string,
  input: { email: string; displayName: string; password?: string },
): Promise<{ portalUser: PortalUserLink; accountCreated: boolean; inviteSent: boolean }> =>
  request<{ portalUser: PortalUserLink; accountCreated: boolean; inviteSent: boolean }>(
    `/api/clients/${clientId}/portal-users`,
    { method: "POST", body: JSON.stringify(input) },
  );

/** Revokes a portal user's link (re-inviting revives it). */
export const revokeClientPortalUser = (
  clientId: string,
  userId: string,
): Promise<{ ok: boolean }> =>
  request<{ ok: boolean }>(`/api/clients/${clientId}/portal-users/${userId}`, { method: "DELETE" });

/** The calling portal user's linked client identity. */
export const getPortalMe = (): Promise<{ portalUser: PortalProfile }> =>
  request<{ portalUser: PortalProfile }>("/api/portal/me");

/** All client-visible documents for the linked client. */
export const getPortalDocuments = (): Promise<{ quotes: PortalQuote[]; invoices: PortalInvoice[] }> =>
  request<{ quotes: PortalQuote[]; invoices: PortalInvoice[] }>("/api/portal/documents");

/** Client self-service acceptance of a sent quote. */
export const portalAcceptQuote = (quoteId: string): Promise<{ quote: QuoteRecord }> =>
  request<{ quote: QuoteRecord }>(`/api/portal/quotes/${quoteId}/accept`, { method: "POST" });

/** Client self-service rejection of a sent quote. */
export const portalRejectQuote = (quoteId: string): Promise<{ quote: QuoteRecord }> =>
  request<{ quote: QuoteRecord }>(`/api/portal/quotes/${quoteId}/reject`, { method: "POST" });

/* ---------- Credential emails (portal-invites-resets epic) ---------- */

/** Requests a password-reset email. Always succeeds; never reveals account existence. */
export const forgotPassword = (email: string): Promise<{ ok: boolean }> =>
  request<{ ok: boolean }>("/api/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });

/** Redeems a set-password invite token. */
export const setPasswordWithToken = (token: string, password: string): Promise<{ ok: boolean }> =>
  request<{ ok: boolean }>("/api/auth/set-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });

/** Redeems a password-reset token. */
export const resetPasswordWithToken = (token: string, password: string): Promise<{ ok: boolean }> =>
  request<{ ok: boolean }>("/api/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });

/** Redeems a magic sign-in link; the response sets the session cookie. */
export const magicLoginWithToken = (token: string): Promise<{ user: { id: string; email: string } }> =>
  request<{ user: { id: string; email: string } }>("/api/auth/magic-login", {
    method: "POST",
    body: JSON.stringify({ token }),
  });

/* ---------- Invoice payments (invoice-payments epic) ---------- */

/** Payment history + balance for one invoice. */
export const getInvoicePayments = (invoiceId: string): Promise<InvoicePaymentsSummary> =>
  request<InvoicePaymentsSummary>(`/api/invoices/${invoiceId}/payments`);

/** Records a payment; the invoice transitions to paid when fully covered. */
export const recordPayment = (
  invoiceId: string,
  input: { amountCents: number; paidOn: string; method?: string; reference?: string },
): Promise<{ payment: InvoicePaymentsSummary["payments"][number]; invoice: InvoiceRecord }> =>
  request<{ payment: InvoicePaymentsSummary["payments"][number]; invoice: InvoiceRecord }>(
    `/api/invoices/${invoiceId}/payments`,
    { method: "POST", body: JSON.stringify(input) },
  );

/* ---------- Super-admin user management ---------- */

/** The client-portal link behind a user account, when one exists. */
export interface AdminPortalLink {
  authUserId: string;
  clientId: string;
  email: string;
  displayName: string;
  createdAt: string;
  revokedAt: string | null;
  clientName: string | null;
}

/** A roster user: package admin fields plus the enriched portal link. */
export interface AdminUserRecord {
  contractor?: ContractorDetails | null;
  id: string;
  email: string;
  displayName: string;
  status: string;
  systemRole: string;
  roles: string[];
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
  readOnly: boolean;
  /** The env-provisioned master admin: status/role/password are locked server-side. */
  isMasterAdmin: boolean;
  portalLink: AdminPortalLink | null;
}

export const getServices = (): Promise<{ services: ServiceRecord[]; categories: string[] }> => request("/api/admin/services");
export const addService = (label: string, category: string): Promise<{ service: ServiceRecord }> => request("/api/admin/services", { method: "POST", body: JSON.stringify({ label, category }) });
export const createContractor = (input: ContractorInput): Promise<{ user: AdminUserRecord }> => request("/api/admin/contractors", { method: "POST", body: JSON.stringify(input) });
export const updateContractor = (id: string, input: ContractorInput): Promise<{ user: AdminUserRecord }> => request(`/api/admin/contractors/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) });

/** One role from the catalogue, with the permissions it grants. */
export interface AdminRoleDefinition {
  name: string;
  permissions: string[];
}

/** One page of the enriched admin roster. */
export interface AdminUsersResult {
  page: number;
  pageSize: number;
  total: number;
  users: AdminUserRecord[];
}

/** Roster list filters (same semantics as the package's list API). */
export interface AdminUsersQuery {
  search?: string;
  status?: string;
  role?: string;
  page?: number;
  pageSize?: number;
  sort?: "name" | "email" | "status" | "lastLogin";
}

/** Lists all users (app users + client invitees) with portal enrichment. */
export const getAdminUsers = (query: AdminUsersQuery = {}): Promise<AdminUsersResult> => {
  const params = new URLSearchParams();
  if (query.search?.trim()) params.set("search", query.search.trim());
  if (query.status) params.set("status", query.status);
  if (query.role) params.set("role", query.role);
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.pageSize && query.pageSize !== 25) params.set("pageSize", String(query.pageSize));
  if (query.sort && query.sort !== "name") params.set("sort", query.sort);
  const qs = params.toString();
  return request<AdminUsersResult>(`/api/admin/users${qs ? `?${qs}` : ""}`);
};

/** The role -> permission catalogue (drives the roles editor). */
export const getAdminRoles = (): Promise<{ roles: AdminRoleDefinition[] }> =>
  request<{ roles: AdminRoleDefinition[] }>("/api/admin/roles");

/** Creates a vendor-side user (admin/manager/user); portal users are invited instead. */
export const adminCreateUser = (input: {
  email: string;
  displayName: string;
  password: string;
  systemRole?: string;
  roles?: string[];
}): Promise<{ user: AdminUserRecord }> =>
  request<{ user: AdminUserRecord }>("/auth/admin/users", {
    method: "POST",
    body: JSON.stringify(input),
  });

/** Sets a user's lifecycle status; bumps their session version (forces re-login). */
export const setAdminUserStatus = (userId: string, status: string): Promise<void> =>
  request<void>(`/auth/admin/users/${userId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });

/** Replaces a user's role set; unknown roles are rejected server-side. */
export const setAdminUserRoles = (userId: string, roles: string[]): Promise<void> =>
  request<void>(`/auth/admin/users/${userId}/roles`, {
    method: "PATCH",
    body: JSON.stringify({ roles }),
  });

/** Sets a new password for a user (min 8 chars server-side); invalidates sessions. */
export const adminResetPassword = (userId: string, password: string): Promise<{ accepted: boolean }> =>
  request<{ accepted: boolean }>(`/auth/admin/users/${userId}/password-reset`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });

/** Re-sends the set-password invite email to an active portal user. */
export const resendPortalInvite = (userId: string): Promise<{ ok: boolean }> =>
  request<{ ok: boolean }>(`/api/admin/users/${userId}/resend-invite`, { method: "POST" });

/** Emails a single-use password-reset link so the user sets their own password. */
export const sendPasswordResetLink = (userId: string): Promise<{ ok: boolean }> =>
  request<{ ok: boolean }>(`/api/admin/users/${userId}/send-password-reset`, { method: "POST" });

/** Emails a single-use sign-in link that signs the user straight in. */
export const sendMagicLoginLink = (userId: string): Promise<{ ok: boolean }> =>
  request<{ ok: boolean }>(`/api/admin/users/${userId}/send-magic-link`, { method: "POST" });

/* ---------- outbound email log (admin) ---------- */

/** One logged outbound-email attempt (recipients/actor already masked server-side). */
export interface EmailLogEntry {
  id: string;
  kind: string;
  to: string[];
  subject: string;
  status: "sent" | "failed" | "logged";
  messageId: string | null;
  smtpResponse: string | null;
  error: string | null;
  bodyPreview: string | null;
  hasHtml: boolean;
  attachmentName: string | null;
  attachmentBytes: number | null;
  createdBy: string | null;
  createdAt: string;
}

/** Log list filters (cursor + status; limit is server-capped). */
export interface EmailLogQuery {
  limit?: number;
  before?: string;
  status?: string;
}

/** Lists the newest outbound-email attempts, newest first. */
export const getEmailLog = (query: EmailLogQuery = {}): Promise<{ entries: EmailLogEntry[] }> => {
  const params = new URLSearchParams();
  if (query.limit) params.set("limit", String(query.limit));
  if (query.before) params.set("before", query.before);
  if (query.status) params.set("status", query.status);
  const qs = params.toString();
  return request<{ entries: EmailLogEntry[] }>(`/api/admin/email-log${qs ? `?${qs}` : ""}`);
};

/* ---------- email template management (admin) ---------- */

/** One documented placeholder tag for a template kind. */
export interface EmailTemplatePlaceholder {
  tag: string;
  description: string;
}

/** One editable email template (override text when present, else built-in). */
export interface EmailTemplateRecord {
  kind: string;
  label: string;
  description: string;
  placeholders: EmailTemplatePlaceholder[];
  subject: string;
  body: string;
  overridden: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** Editor text for save/preview/test (plain text with {{tag}} tokens). */
export interface EmailTemplateDraft {
  subject?: string;
  body?: string;
  to?: string;
}

/** The full template catalogue. */
export const getEmailTemplates = (): Promise<{ templates: EmailTemplateRecord[] }> =>
  request<{ templates: EmailTemplateRecord[] }>("/api/admin/email-templates");

/** Saves an override for a kind (validated server-side). */
export const updateEmailTemplate = (
  kind: string,
  input: { subject: string; body: string },
): Promise<{ template: EmailTemplateRecord }> =>
  request<{ template: EmailTemplateRecord }>(`/api/admin/email-templates/${kind}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });

/** Deletes the override; the built-in template renders again. */
export const resetEmailTemplate = (kind: string): Promise<{ template: EmailTemplateRecord }> =>
  request<{ template: EmailTemplateRecord }>(`/api/admin/email-templates/${kind}`, {
    method: "DELETE",
  });

/** Renders (never sends) the current-or-draft template with sample data. */
export const previewEmailTemplate = (
  kind: string,
  draft: EmailTemplateDraft = {},
): Promise<{ subject: string; body: string; html: string }> =>
  request<{ subject: string; body: string; html: string }>(
    `/api/admin/email-templates/${kind}/preview`,
    { method: "POST", body: JSON.stringify(draft) },
  );

/** Sends a rendered template with sample data to the given (or own) address. */
export const sendTestEmail = (
  kind: string,
  draft: EmailTemplateDraft = {},
): Promise<{ delivered: boolean; to: string }> =>
  request<{ delivered: boolean; to: string }>(`/api/admin/email-templates/${kind}/test`, {
    method: "POST",
    body: JSON.stringify(draft),
  });

/* ---------- document settings (admin) ---------- */

/** Vendor-editable payment/remittance details printed on invoice PDFs. */
export interface DocumentSettings {
  paymentTerms: string;
  bankAccountName: string;
  bankBsb: string;
  bankAccountNumber: string;
  paymentReference: string;
  otherPaymentMethods: string;
  invoiceFooterNote: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

/**
 * The letterhead identity as the server will print it. Read-only here: it is
 * deployment configuration (env), not a per-user setting.
 */
export interface DocumentBusinessIdentity {
  name: string;
  abn: string;
  address: string;
  email: string;
  phone: string;
  hasLogo: boolean;
}

/** Current document settings plus the read-only letterhead identity. */
export const getDocumentSettings = (): Promise<{
  settings: DocumentSettings;
  business: DocumentBusinessIdentity;
}> => request("/api/admin/document-settings");

/** Replaces the document settings; the whole record is submitted every save. */
export const saveDocumentSettings = (
  settings: Partial<DocumentSettings>,
): Promise<{ settings: DocumentSettings }> =>
  request("/api/admin/document-settings", {
    method: "PUT",
    body: JSON.stringify(settings),
  });

/* ---------- Card collaboration (comments, members, mentions) ---------- */

/** Lists a job's live comments (oldest first). */
export const getJobComments = (jobId: string): Promise<{ comments: CommentRecord[] }> =>
  request<{ comments: CommentRecord[] }>(`/api/jobs/${jobId}/comments`);

/** Posts a comment (markdown source; mentions as `@[Name](user:ID)`). */
export const createJobComment = (jobId: string, body: string): Promise<{ comment: CommentRecord }> =>
  request<{ comment: CommentRecord }>(`/api/jobs/${jobId}/comments`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });

/** Soft-deletes a comment (vendor write, or the author's own). */
export const deleteJobComment = (jobId: string, commentId: string): Promise<void> =>
  request<void>(`/api/jobs/${jobId}/comments/${commentId}`, { method: "DELETE" });

/**
 * Candidates for @mentions and card membership on one job: Active vendor staff
 * plus the portal users of the job's client organisation.
 */
export const getMentionableUsers = (jobId: string): Promise<{ users: MentionableUser[] }> =>
  request<{ users: MentionableUser[] }>(`/api/jobs/${jobId}/mentionable`);

/** Lists a job's members (vendor callers only). */
export const getJobMembers = (jobId: string): Promise<{ members: MemberView[] }> =>
  request<{ members: MemberView[] }>(`/api/jobs/${jobId}/members`);

/** Replaces a job's member list (vendor only); full-list semantics. */
export const setJobMembers = (jobId: string, memberIds: string[]): Promise<{ members: MemberView[] }> =>
  request<{ members: MemberView[] }>(`/api/jobs/${jobId}/members`, {
    method: "PUT",
    body: JSON.stringify({ members: memberIds }),
  });
